/**
 * Theme engine (ANTÉ handoff): light is the brand default, dark re-keys the
 * tokens via <html data-theme>. Mode 'system' follows the device — inside
 * Telegram that's WebApp.colorScheme (+ themeChanged event), on the plain web
 * it's prefers-color-scheme. A manual choice in Settings overrides and is
 * persisted per device in localStorage.
 */
import { useSyncExternalStore } from 'react';
import { tg } from './telegram';

export type ThemeMode = 'system' | 'dark' | 'light';
export type ResolvedTheme = 'dark' | 'light';

const STORAGE_KEY = 'ante_theme';
/** Экранный фон по теме — уходит в Telegram setBackgroundColor + <meta theme-color>. */
const BG: Record<ResolvedTheme, string> = { dark: '#0a1024', light: '#f7f9fe' };
/** Шапки всегда фирменно-синие — статус-бар Telegram красим в бренд. */
const HEADER = '#145efa';

let mode: ThemeMode = readStored();
const listeners = new Set<() => void>();

function readStored(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'dark' || v === 'light' ? v : 'system';
  } catch {
    return 'system';
  }
}

function systemTheme(): ResolvedTheme {
  if (tg?.colorScheme === 'light' || tg?.colorScheme === 'dark') return tg.colorScheme;
  try {
    // Unknown/unsupported ⇒ light (the brand default look).
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

export function resolvedTheme(): ResolvedTheme {
  return mode === 'system' ? systemTheme() : mode;
}

export function getThemeMode(): ThemeMode {
  return mode;
}

function applyTheme(): void {
  const t = resolvedTheme();
  document.documentElement.dataset.theme = t;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', BG[t]);
  tg?.setHeaderColor(HEADER);
  tg?.setBackgroundColor(BG[t]);
  listeners.forEach((l) => l());
}

export function setThemeMode(next: ThemeMode): void {
  mode = next;
  try {
    if (next === 'system') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* private mode etc. — theme still applies for the session */
  }
  applyTheme();
}

/** Apply the theme immediately (call before render) and follow device changes. */
export function initTheme(): void {
  applyTheme();
  tg?.onEvent?.('themeChanged', () => {
    if (mode === 'system') applyTheme();
  });
  try {
    window.matchMedia('(prefers-color-scheme: light)').addEventListener('change', () => {
      if (mode === 'system') applyTheme();
    });
  } catch {
    /* old browsers: no live follow; manual switch still works */
  }
}

/** Reactive current mode for the Settings screen. */
export function useThemeMode(): ThemeMode {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => mode,
  );
}
